import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Loader2, Users, ChevronDown, ChevronRight, Mail, Phone, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DocumentsMembreGED } from './MembresTable/DocumentsMembreGED';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nomMembre = (m) => m.raison_sociale || [m.prenom, m.nom].filter(Boolean).join(' ') || m.email;
const euros = (v) => (v || v === 0 ? `${Number(v).toLocaleString('fr-FR')} €` : '—');

function Souscriptions({ items, mid }) {
  if (!items.length) return <p className="text-sm text-slate-500 py-2" data-testid={`expert-membre-aucune-souscription-${mid}`}>Aucun contrat ni souscription.</p>;
  return (
    <table className="w-full text-sm mt-2" data-testid={`expert-membre-souscriptions-${mid}`}>
      <thead><tr className="text-left text-slate-500 border-b">
        <th className="py-2">N° police</th><th>Dispositif</th><th>Statut</th><th>Montant</th><th>Fin</th>
      </tr></thead>
      <tbody>
        {items.map((s) => (
          <tr key={s.id} className="border-b last:border-0" data-testid={`expert-souscription-row-${s.id}`}>
            <td className="py-2 font-mono text-xs">{s.numero_police || '—'}</td>
            <td>{s.type_dispositif || s.type_souscription || '—'}</td>
            <td><Badge variant="outline">{(s.statut || '—').replace(/_/g, ' ')}</Badge></td>
            <td>{euros(s.montant_total)}</td>
            <td>{s.date_fin ? new Date(s.date_fin).toLocaleDateString('fr-FR') : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LigneMembre({ m, token }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div className="border rounded-lg" data-testid={`expert-membre-row-${m.id}`}>
      <button type="button" onClick={() => setOuvert(!ouvert)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors" data-testid={`expert-membre-toggle-${m.id}`}>
        {ouvert ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate">{nomMembre(m)}</p>
          <p className="text-xs text-slate-500 flex flex-wrap gap-x-4">
            {m.siret && <span>SIRET {m.siret}</span>}
            {m.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</span>}
            {m.telephone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{m.telephone}</span>}
          </p>
        </div>
        {m.ftpe ? (
          <Badge className="bg-emerald-600 text-white font-semibold" data-testid={`expert-membre-ftpe-${m.id}`}><Shield className="h-3 w-3 mr-1" />FTPE jusqu'au {new Date(m.ftpe.date_expiration).toLocaleDateString('fr-FR')}</Badge>
        ) : <Badge variant="outline" data-testid={`expert-membre-ftpe-${m.id}`}>Non adhérent FTPE</Badge>}
        <Badge variant="secondary">{m.souscriptions.length} contrat(s)</Badge>
      </button>
      {ouvert && (
        <div className="px-4 pb-4 space-y-4">
          <Souscriptions items={m.souscriptions} mid={m.id} />
          <DocumentsMembreGED membreId={m.id} token={token} userRole="expert" />
        </div>
      )}
    </div>
  );
}

export default function ExpertMembresConfies() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/ged-dossiers/membre/expert/mes-membres`, { headers: getAuthHeader() })
      .then((r) => setData(r.data.membres))
      .catch((e) => setErreur(e.response?.data?.detail || 'Impossible de charger vos membres confiés'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const liste = (data || []).filter((m) => `${nomMembre(m)} ${m.siret || ''} ${m.email || ''}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card data-testid="expert-membres-confies">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" />Membres confiés</CardTitle>
        <CardDescription>Membres qui vous sont rattachés, avec leurs contrats et souscriptions (consultation seule).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {erreur && <p className="text-sm text-red-600" data-testid="expert-membres-erreur">{erreur}</p>}
        {!data && !erreur && <Loader2 className="h-6 w-6 animate-spin text-blue-600" data-testid="expert-membres-loading" />}
        {data && (
          <>
            <Input placeholder="Rechercher un membre (nom, SIRET, email)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="expert-membres-search" />
            <p className="text-sm text-slate-600" data-testid="expert-membres-total">{liste.length} membre(s)</p>
            {liste.length === 0 && <p className="text-sm text-slate-500" data-testid="expert-membres-vide">Aucun membre ne vous est confié pour le moment.</p>}
            {liste.map((m) => <LigneMembre key={m.id} m={m} token={(getAuthHeader().Authorization || '').replace('Bearer ', '')} />)}
          </>
        )}
      </CardContent>
    </Card>
  );
}
